import { z } from 'zod';

import i18n from '@/lib/i18n';
import { institutionDetailsIndividualBaseSchema, institutionDetailsNationalClientIdentifierSchema } from '@/services/api/profile';
import { getNationalClientIdentifierFormatError, reportNationalClientIdentifierFormatError } from '@/services/api/profile/national-client-identifier-format';

export const institutionIndividualSchema = institutionDetailsIndividualBaseSchema
  .omit({
    dateOfBirth: true,
  })
  .extend({
    dateOfBirth: z.object({
      day: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('institutionDetails.individuals.individual.errors.dateOfBirthDay'),
      }),
      month: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('institutionDetails.individuals.individual.errors.dateOfBirthMonth'),
      }),
      year: z.string().regex(/^\d{4}$/, {
        error: i18n.t('institutionDetails.individuals.individual.errors.dateOfBirthYear'),
      }),
    }),
  })
  .and(institutionDetailsNationalClientIdentifierSchema)
  .superRefine(async ({ nationality, nationalClientIdentifier, nationalClientIdentifierType }, ctx) => {
    const error = await getNationalClientIdentifierFormatError({
      country: nationality,
      identifier: nationalClientIdentifier,
      identifierType: nationalClientIdentifierType,
    });

    if (error) ctx.addIssue({ code: 'custom', message: error, path: ['nationalClientIdentifier'] });
  });

export type InstitutionIndividual = z.infer<typeof institutionIndividualSchema>;

export type InstitutionIndividualOrDefaults = InstitutionIndividual | Partial<InstitutionIndividual>;

export type DateOfBirthFieldKey = keyof InstitutionIndividual['dateOfBirth'];

export const institutionDetailsIndividualDateOfBirthFields = ['day', 'month', 'year'] satisfies DateOfBirthFieldKey[];

export const reportIndividualNationalClientIdentifierFormatErrors = (individuals: InstitutionIndividualOrDefaults[] = []) =>
  Promise.all(
    individuals.map(({ nationality, nationalClientIdentifier, nationalClientIdentifierType }) =>
      reportNationalClientIdentifierFormatError({ country: nationality, identifier: nationalClientIdentifier, identifierType: nationalClientIdentifierType }),
    ),
  );
